/**
 * SWW — Spelling Werkwoorden rulebook.
 *
 * Sourced from DEFAULT_POLICY (SpellingWW entries — has checkStrategyMinTier=7) +
 * MODULE_AUTHORING_GUIDE §7.
 */

import type { ModuleRulebook } from './types'

export const SWW_RULEBOOK: ModuleRulebook = {
  moduleId: 'SpellingWW',
  shortCode: 'SWW',
  displayName: 'Spelling Werkwoorden',

  didactics: {
    principles: [
      'stam_eerst',
      'kofschip_regel',
      'dt_logica',
      'tijd_persoon_vorm',
    ],
    schoolbookReferences: ['Staal Spelling', 'Taal Actief'],
    misconceptionDomain: 'sww',
    coreSummary:
      'Werkwoordspelling: stam vinden, kofschip-regel, dt-regel, tegenwoordige vs. verleden tijd vs. voltooid deelwoord.',
  },

  notation: {},

  content: {
    distractorMisconceptionPattern:
      'stam|kofschip|dt-regel|verdubbel|persoonsvorm|infinitief|voltooid deelwoord',
  },

  explanation: {
    requireRuleReference: true,
    checkStrategyMinTier: 7,
    preferredStrategyKeywords: [
      'stam',
      'kofschip',
      'dt-regel',
      "'t kofschip",
      'persoonsvorm',
    ],
  },

  visuals: {
    preferredAidTypes: ['kofschipMnemonic', 'verbTable', 'rule'],
    familyVisualHints: [
      // — Leenwoorden: specifieke regel-hints voor generieke VT/VD-hint —
      {
        familyIdPattern: '^SWW-LEEN-ENG-KOF',
        preferredSupport: 'kofschipMnemonic',
        note: 'Engelse leenwoorden + kofschip: kofschip-regel geldt op de Engels-uitgesproken stam (face → faceTE).',
      },
      {
        familyIdPattern: '^SWW-LEEN-ENG-STAM',
        preferredSupport: 'verbTable',
        note: 'Engels leenwoord-stam: stamvinding wijkt af (download → ik download, niet downloaded); werkwoordtabel als anker.',
      },
      {
        familyIdPattern: '^SWW-LEEN-FRANS',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Franse leenwoorden: regel-kaart voor stam-spelling (controleer → controleerde, niet controlerde).',
      },

      // — BN-VD (bijvoeglijk gebruikt voltooid deelwoord) —
      {
        familyIdPattern: '^SWW-BN-VD-',
        preferredSupport: 'verbTable',
        note: 'Voltooid deelwoord als bijvoeglijk naamwoord: tabel toont VD-vorm + verbuiging (-e).',
      },

      // — Zwakke VT + kofschip-VD (regel-kern) —
      {
        familyIdPattern: '^SWW-VT-(ZWAK-KOF|DUBBEL)$|^SWW-VD-(KOF|HOMOFOON)',
        preferredSupport: 'kofschipMnemonic',
        note: 'Zwakke werkwoorden: kofschip-ezelsbruggetje voor VT (te/de) en VD (-t/-d).',
      },

      // — Sterke / onregelmatige werkwoorden: paradigma-tabel —
      {
        familyIdPattern:
          '^SWW-VT-(STERK|ONREGELMATIG|ZIJN-HEBBEN|ZULLEN-KUNNEN|VZFS)|^SWW-VD-(STERK|HULPWW|ZONDER-GE|BN-)',
        preferredSupport: 'verbTable',
        note: 'Sterke/onregelmatige werkwoorden: verbTable met volledig paradigma (TT/VT/VD) — geen kofschip.',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      'multiple_choice',
      'fill_in',
      'error_spotting',
      'verb_conjugation_flow',
      'sentence_error_judge',
    ],
    customTypeRoles: [
      {
        type: 'verb_conjugation_flow',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 7, max: 16 },
        reason:
          'Stap-voor-stap vervoegen (stam → tijd → persoon → kofschip-check) maakt de regel-keten expliciet — DOK 3.',
      },
      {
        type: 'error_spotting',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 7, max: 16 },
        reason:
          'Fout opsporen in geschreven werkwoordsvorm: meta-cognitie over de regel, niet alleen toepassen.',
      },
      {
        type: 'sentence_error_judge',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 9, max: 16 },
        reason:
          'Goed/fout-oordeel op hele zin met werkwoordsvorm in context — combineert tijd, persoon en kofschip.',
      },
    ],
    familyTypeHints: [
      {
        familyIdPattern:
          '^SWW-TT-(STAM|DSTAM|INV-JIJ|INV-TRICK|MEERVOUD|GEBIEDENDE)',
        preferredTypes: ['fill_in', 'verb_conjugation_flow', 'error_spotting'],
        discouragedTypes: ['true_false_series', 'estimation_slider'],
        note: 'TT-vervoeging vereist actieve productie van de vorm — geen waar/onwaar of slider.',
      },
      {
        familyIdPattern: '^SWW-TT-ZIJN-HEBBEN',
        preferredTypes: ['fill_in', 'multiple_choice', 'verb_conjugation_flow'],
        discouragedTypes: ['true_false_series'],
        note: 'Onregelmatige vervoeging (zijn/hebben): exact invullen of MC tussen vormen.',
      },
      {
        familyIdPattern:
          '^SWW-VT-(STERK|ONREGELMATIG|ZIJN-HEBBEN|ZULLEN-KUNNEN|VZFS)',
        preferredTypes: ['fill_in', 'multiple_choice', 'verb_conjugation_flow'],
        discouragedTypes: ['true_false_series'],
        note: 'Onregelmatige/sterke VT: vorm-recall via invullen, niet via oordeel.',
      },
      {
        familyIdPattern: '^SWW-VT-(DUBBEL|ZWAK-KOF)',
        preferredTypes: ['fill_in', 'verb_conjugation_flow', 'error_spotting'],
        discouragedTypes: ['true_false_series'],
        note: 'Zwakke VT met kofschip-check: regel-keten stap-voor-stap, geen oordeel.',
      },
      {
        familyIdPattern: '^SWW-VD-(KOF|STERK|HULPWW|ZONDER-GE|HOMOFOON)',
        preferredTypes: [
          'fill_in',
          'verb_conjugation_flow',
          'error_spotting',
          'sentence_error_judge',
        ],
        discouragedTypes: ['true_false_series'],
        note: 'Voltooid deelwoord: kofschip + ge- prefix — actief invullen of fout opsporen.',
      },
      {
        familyIdPattern: '^SWW-BN-VD-(DUBBEL|KORT)',
        preferredTypes: ['fill_in', 'error_spotting', 'multiple_choice'],
        discouragedTypes: ['true_false_series'],
        note: 'Bijvoeglijk voltooid deelwoord (verdubbelen/verkorten): invullen of fout opsporen.',
      },
      {
        familyIdPattern: '^SWW-LEEN-',
        preferredTypes: ['fill_in', 'multiple_choice', 'error_spotting'],
        discouragedTypes: ['true_false_series', 'estimation_slider'],
        note: 'Leenwoord-vervoeging (Eng/Fr): vorm + kofschip op vreemde stam — actieve productie.',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'dt-overgeneralize',
      label: 'dt-overgeneralisatie',
      description:
        '"hij werkdt" — d en t gestapeld door regel verkeerd toe te passen.',
    },
    {
      id: 'kofschip-violation',
      label: 'Kofschip-fout',
      description:
        'Bij verleden tijd "te"/"de" verwisseling — kerntrap bij zwakke werkwoorden.',
    },
    {
      id: 'stem-error',
      label: 'Stam verkeerd bepaald',
      description:
        'Stam met -en of -t i.p.v. infinitief minus -en — beïnvloedt alle vervolg-regels.',
    },
  ],

  keyRules: [
    'Kofschip-regel altijd vermelden in uitleg',
    'Stam-berekening stap voor stap uitleggen',
    'Trap-distractor: dt-fout (werkt/werkdt)',
    'Hints verplicht voor alle SWW-families',
    'Vanaf tier 7: controlestrategie ("klopt de tijd? klopt de persoon?")',
  ],

  // GSR-23 (PR-9): SWW-distractors zijn werkwoordsvormen (werkt/werkte/gewerkt),
  // niet getallen. De numeric-distractor parity check zou hier altijd false-positives
  // produceren — daarom expliciet uitgezet als didactische opt-out.
  qualityChecks: {
    skipNumericDistractorParity: true,
  },

  // GSR-25 (PR-9): per-module override copy migrated from inline
  // `moduleMessages` maps in didacticDepth.ts.
  qualityMessages: {
    explanationDepthRequirement:
      'SWW explanation must cite spelling rule (stam, kofschip, dt-regel)',
    distractorMisconceptionRequirement:
      'SWW distractors should reference spelling rule error',
  },

  authoringGuideAnchor: '#7-sww--spelling-werkwoorden',
}
